import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate } from "@/lib/api";
import { PageHeader, EmptyState, Money, TypeBadge } from "@/components/common";
import { COST_CATEGORIES, can } from "@/lib/labels";
import { CostDialog } from "@/components/dialogs";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Costs() {
  const { user } = useAuth();
  const [costs, setCosts] = useState(null);
  const [cars, setCars] = useState([]);
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);

  const load = () => {
    Promise.all([api.get("/costs"), api.get("/cars")]).then(([co, ca]) => {
      setCosts(co.data);
      setCars(ca.data);
    });
  };
  useEffect(() => { load(); }, []);

  const eligible = cars;
  const filtered = category === "all" ? costs : costs.filter((c) => c.category === category);
  const total = (filtered || []).reduce((a, c) => a + c.amount, 0);

  const remove = async (id) => {
    if (!window.confirm("Hapus biaya ini?")) return;
    try {
      await api.delete(`/costs/${id}`);
      toast.success("Biaya dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="costs-page">
      <PageHeader title="Biaya Mobil" subtitle="Body repair, mesin, dokumen & biaya lain per unit">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="cost-category-filter" className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            <SelectItem value="all">Semua Kategori</SelectItem>
            {Object.entries(COST_CATEGORIES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {can(user, "input_biaya_mobil") && (
          <Button data-testid="add-cost-button" onClick={() => setOpen(true)} disabled={eligible.length === 0} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md">
            <Plus className="w-4 h-4 mr-1" /> Tambah Biaya
          </Button>
        )}
      </PageHeader>

      {!costs ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : costs.length === 0 ? (
        <EmptyState icon={Wrench} title="Belum ada biaya tercatat" desc="Catat biaya perbaikan & dokumen per mobil" />
      ) : (
        <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up">
          <table className="w-full text-sm min-w-[700px]" data-testid="costs-table">
            <thead>
              <tr className="border-b border-[#1A1A22] text-left">
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Mobil</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Kategori</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Keterangan</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tanggal</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Dicatat Oleh</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Jumlah</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                  <td className="px-4 py-3">
                    <Link to={`/mobil/${c.car_id}`} className="font-medium text-zinc-100 hover:text-blue-500 transition-colors duration-200">{c.car_name}</Link>
                    {c.created_by_name && <p className="text-[10px] text-zinc-600">dicatat oleh {c.created_by_name}</p>}
                  </td>
                  <td className="px-4 py-3"><TypeBadge label={COST_CATEGORIES[c.category]} /></td>
                  <td className="px-4 py-3 text-zinc-400">{c.description || "-"}</td>
                  <td className="px-4 py-3 text-zinc-400">{fmtDate(c.date)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{c.created_by_name || "-"}</td>
                  <td className="px-4 py-3 text-right"><Money value={c.amount} className="text-zinc-100" /></td>
                  <td className="px-4 py-3 text-right">
                    {user?.role === "admin" && (
                      <button data-testid={`delete-cost-${c.id}`} onClick={() => remove(c.id)} className="text-zinc-600 hover:text-rose-500 transition-colors duration-200">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-[#050505]/50">
                <td colSpan={5} className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Total</td>
                <td className="px-4 py-3 text-right"><Money value={total} className="text-blue-500 font-semibold" /></td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <CostDialog open={open} onOpenChange={setOpen} cars={eligible} onSaved={load} />
    </div>
  );
}
